import { useRef, useEffect } from 'react';
import * as H from 'history';
import useFeatureFlag from './useFeatureFlag';
import { FeatureFlagName } from 'shared/enums';
import { useApolloClient } from '@apollo/client';
import { useSidePanel } from 'shared/components/SidePanel/state/providers/SidePanelProvider';
import useZendesk from './useZendesk';
import useClinicUser from './useClinicUser';
import { useRecap } from 'shared/providers/RecapProvider/RecapProvider';

const TEN_MINUTES = 1000 * 60 * 10;
const ONE_HOUR = 1000 * 60 * 60;
const FOUR_HOURS = ONE_HOUR * 4;

interface IUseAutoPageReload {
  history: H.History;
  isDisabled?: boolean;
}

const useAutoPageReload = ({ history, isDisabled }: IUseAutoPageReload): void => {
  const apolloClient = useApolloClient();
  const activeStartTime = useRef<number>(Date.now());
  const shouldRefreshWhenVisible = useRef<boolean>(false);
  const { isFeatureEnabled } = useFeatureFlag();
  const { isInvoicingPanelDirty } = useSidePanel();
  const hasDisableAutoRefreshFeatureFlag = isFeatureEnabled(FeatureFlagName.DisableAutoRefresh);
  const { clinicUser } = useClinicUser();
  const { isZendeskOpen } = useZendesk(clinicUser);
  const { isRecapActive } = useRecap();

  useEffect(() => {
    if (hasDisableAutoRefreshFeatureFlag || isDisabled || isInvoicingPanelDirty) {
      return;
    }

    const onVisibilityChange = (): void => {
      if (
        document.visibilityState === 'visible' &&
        shouldRefreshWhenVisible.current &&
        !isZendeskOpen &&
        !isRecapActive
      ) {
        console.log('AUTOMATIC RELOAD: 1-hour reload  on visibilityState: visible');
        location.reload();
      } else if (document.visibilityState === 'hidden' && Date.now() - activeStartTime.current >= ONE_HOUR) {
        console.log('Queuing up reload on next visibilityState: visible');
        shouldRefreshWhenVisible.current = true;
      }
    };

    // Force reload in the background every hour
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Force reload on page navigation every 4 hours
    const unlisten = history.listen(() => {
      if (Date.now() - activeStartTime.current >= FOUR_HOURS) {
        if (isZendeskOpen || isRecapActive) {
          console.log('Zendesk or AI Recap is open, not reloading');
          return;
        }
        console.log('AUTOMATIC RELOAD 4-hour reload on navigation');
        location.reload();
      }
    });

    return (): void => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      unlisten();
    };
  }, [history, isDisabled, hasDisableAutoRefreshFeatureFlag, isInvoicingPanelDirty, isZendeskOpen, isRecapActive]);

  useEffect(() => {
    if (!hasDisableAutoRefreshFeatureFlag) {
      return;
    }

    // Force cache reload on page navigation every 10 minutes (if feature flag is enabled)
    const unlisten = history.listen(async () => {
      if (Date.now() - activeStartTime.current >= TEN_MINUTES) {
        console.log('10-minute cache reload on navigation');
        activeStartTime.current = Date.now();
        await apolloClient.resetStore();
      }
    });

    return (): void => {
      unlisten();
    };
  }, [hasDisableAutoRefreshFeatureFlag, history, apolloClient]);
};

export default useAutoPageReload;
